import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
} from '@chakra-ui/react';
import { LineChart, PieChart, BarChart } from './ChartCard';

const UserDetailModal = ({ isOpen, onClose, user }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.100', 'gray.700');

  if (!user) return null;

  // Dummy data for user activity (refined for chart)
  const userActivityData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Daily Logins',
        data: [user.usage > 50 ? 4 : 2, user.usage > 60 ? 3 : 1, user.usage > 70 ? 5 : 2, user.usage > 40 ? 2 : 1, user.usage > 80 ? 6 : 3, user.usage > 55 ? 3 : 1, user.usage > 65 ? 4 : 2], // Dummy data based on usage
        borderColor: useColorModeValue('brand.500', 'brand.300'),
        backgroundColor: useColorModeValue('brand.500', 'brand.300'),
        tension: 0.3,
        fill: false,
      },
    ],
  };

  // Dummy data for user purchase distribution (refined for chart)
  // Analyze user.purchaseHistory to generate meaningful data
  const purchaseCategories = user.purchaseHistory?.reduce((acc, item) => {
    const category = item.item.split(' ')[0]; // Simple category extraction
    acc[category] = (acc[category] || 0) + item.amount;
    return acc;
  }, {}) || {};

  const userPurchaseDistributionData = {
    labels: Object.keys(purchaseCategories),
    datasets: [
      {
        data: Object.values(purchaseCategories),
        backgroundColor: [
          useColorModeValue('teal.400', 'teal.300'),
          useColorModeValue('red.400', 'red.300'),
          useColorModeValue('blue.400', 'blue.300'),
          useColorModeValue('purple.400', 'purple.300'),
          useColorModeValue('orange.400', 'orange.300'),
        ],
        borderColor: useColorModeValue('gray.100', 'gray.700'),
        borderWidth: 1,
      },
    ],
  };

  // Dummy data for user statistics (kept some for display)
  const userStats = {
    totalPurchases: user.purchaseHistory?.length || 0,
    totalSpent: user.purchaseHistory?.reduce((sum, item) => sum + item.amount, 0) || 0,
    averageOrderValue: user.purchaseHistory?.length > 0 ? (user.purchaseHistory.reduce((sum, item) => sum + item.amount, 0) / user.purchaseHistory.length).toFixed(2) : 0,
    lastPurchase: user.purchaseHistory?.length > 0 ? user.purchaseHistory[user.purchaseHistory.length - 1].date : 'N/A',
    itemsInCart: Math.floor(user.usage / 10), // Dummy data
    wishlistItems: Math.floor(user.usage / 5), // Dummy data
  };

  // Dummy data for user activity table
  const userActivityTableData = [
    { date: '2024-03-20', action: 'Login', details: 'Logged in from Chrome' },
    { date: '2024-03-19', action: 'Purchase', details: `Bought item ID ${user.purchaseHistory?.[0]?.id || 'N/A'}` },
    { date: '2024-03-18', action: 'Profile Update', details: 'Updated profile picture' },
    { date: '2024-03-17', action: 'Viewed Product', details: 'Viewed Product details page' },
  ].filter(activity => new Date(activity.date) <= new Date(user.lastActive)); // Filter based on last active date

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg={useColorModeValue('white', 'gray.800')} color={useColorModeValue('gray.800', 'white')}>
        <ModalHeader borderBottom="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
          <HStack spacing={4}>
            <Avatar size="lg" name={user.name} />
            <Box>
              <Heading size="md">{user.name}</Heading>
              <Badge colorScheme={user.status === 'active' ? 'green' : 'red'}>
                {user.status}
              </Badge>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs variant="enclosed">
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Activity</Tab>
              <Tab>Statistics</Tab>
              <Tab>Settings</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <LineChart data={userActivityData} title="Weekly Activity" />
                    <PieChart data={userPurchaseDistributionData} title="Purchase Distribution" />
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                      <CardBody>
                        <Stat>
                          <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Total Purchases</StatLabel>
                          <StatNumber>{userStats.totalPurchases}</StatNumber>
                          {/* Add StatHelpText and StatArrow if you have historical data */}
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                      <CardBody>
                        <Stat>
                          <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Total Spent</StatLabel>
                          <StatNumber>${userStats.totalSpent}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                      <CardBody>
                        <Stat>
                          <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Avg Order Value</StatLabel>
                          <StatNumber>${userStats.averageOrderValue}</StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              <TabPanel>
                <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                  <CardBody>
                    <Box overflowX="auto">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Date</Th>
                            <Th>Action</Th>
                            <Th>Details</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {userActivityTableData.map((activity, index) => (
                            <Tr key={index}>
                              <Td>{new Date(activity.date).toLocaleDateString()}</Td>
                              <Td>
                                <Badge colorScheme="purple">{activity.action}</Badge>
                              </Td>
                              <Td>{activity.details}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Items in Cart</StatLabel>
                        <StatNumber>{userStats.itemsInCart}</StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Wishlist Items</StatLabel>
                        <StatNumber>{userStats.wishlistItems}</StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                  <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel color={useColorModeValue('gray.500', 'gray.400')}>Last Purchase</StatLabel>
                        <StatNumber>
                          {userStats.lastPurchase === 'N/A' ? 'N/A' : new Date(userStats.lastPurchase).toLocaleDateString()}
                        </StatNumber>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </TabPanel>

              <TabPanel>
                <Card bg={cardBg} boxShadow="md" borderColor={cardBorder} borderWidth="1px">
                  <CardBody>
                    <VStack align="start" spacing={4}>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Email:</Text> {user.email}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Joined:</Text> {new Date(user.createdAt).toLocaleDateString()}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Last Active:</Text> {new Date(user.lastActive).toLocaleString()}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Vendor:</Text> {user.vendor}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Role:</Text> {user.role}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Notifications:</Text> {user.preferences?.notifications ? 'Enabled' : 'Disabled'}</Text>
                      <Text><Text as="strong" color={useColorModeValue('gray.700', 'gray.300')}>Preferred Theme:</Text> {user.preferences?.theme || 'System'}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserDetailModal; 